/**
 * The read-only chips standing in for an ask-user card's choices wherever one
 * is previewed rather than answered — the Outbox hover card, the For You
 * conversation preview. Both asks render through this, so a question and a
 * feedback card preview as the same object; only the words inside the chips
 * differ.
 *
 * Nothing here is answerable, so the chips carry no selection state and no
 * accent tint — the variant colour lives on the badge beside them, and tinting
 * an inert chip would read as an answer already given. The dashed trailing chip
 * is the "Other…" row every ask-user card ends with, kept so a preview never
 * implies the listed options are all that's on offer.
 */

/** One question and the choices under it, flattened from either tool's input. */
export interface AskUserOptionsGroup {
  /** The question's `header` — stable key and the group's accessible name. */
  header: string
  /** The question sentence, suppressed when the host title already says it. */
  prompt: string
  /** Chip labels. May be empty: a free-text-only ask keeps just the dashed chip. */
  options: Array<string>
}

export function AskUserOptionsPreview({
  groups,
  showPrompts,
}: {
  groups: Array<AskUserOptionsGroup>
  showPrompts: boolean
}) {
  return (
    <div className="flex min-w-0 flex-col gap-3">
      {groups.map((group) => (
        <div key={group.header} className="flex min-w-0 flex-col gap-1.5">
          {showPrompts && <p className="text-foreground text-[13px] font-medium leading-normal">{group.prompt}</p>}
          <div aria-label={group.header} className="flex flex-wrap gap-1.5">
            {group.options.map((option) => (
              <span
                key={option}
                className="bg-foreground/[4%] text-foreground-secondary max-w-full truncate rounded-full border px-2 py-1 text-[11px] leading-none"
              >
                {option}
              </span>
            ))}
            <span className="text-foreground-placeholder rounded-full border border-dashed px-2 py-1 text-[11px] leading-none">
              Other…
            </span>
          </div>
        </div>
      ))}
    </div>
  )
}
